import type {
  MatchdayDirectorGddDefaultAveragesKey,
  MatchdayDirectorGddRanges,
  MatchdayDirectorGddTier,
} from '@/types';

/**
 * Pure helpers for the Matchday-Director GDD ranges editor: tier labels, the
 * live help text and the preview.
 *
 * The preview mirrors the server's rule (the backend's matchday-director
 * ranges util). If the two ever disagree, the page shows an admin something
 * other than what the game gets, so change both together.
 */

/** A value while it is being edited: any number may be missing, or null once its box is emptied. */
type Draft<T> = T extends number
  ? number | null | undefined
  : { [K in keyof T]?: Draft<T[K]> | null };

export type GddRangesDraft = Draft<MatchdayDirectorGddRanges>;
export type GddThresholdsDraft = GddRangesDraft['tierThresholds'];

export type GddSide = 'runRate' | 'economy';

export interface GddTierInfo {
  key: MatchdayDirectorGddTier;
  label: string;
  /** The `difficulty` the game receives for this tier, and the game's name for it. */
  difficulty: 0 | 1 | 2 | 3;
  difficultyName: 'easy' | 'medium' | 'hard' | 'expert';
}

export const GDD_TIERS: readonly GddTierInfo[] = [
  { key: 'easy', label: 'Easy', difficulty: 0, difficultyName: 'easy' },
  { key: 'medium', label: 'Medium', difficulty: 1, difficultyName: 'medium' },
  { key: 'hard', label: 'Hard', difficulty: 2, difficultyName: 'hard' },
  { key: 'very_hard', label: 'Very Hard', difficulty: 3, difficultyName: 'expert' },
];

export const GDD_DEFAULT_AVERAGE_ROWS: readonly {
  key: MatchdayDirectorGddDefaultAveragesKey;
  label: string;
}[] = [
  { key: 'overs1', label: '1 over' },
  { key: 'overs3', label: '3 overs' },
  { key: 'overs5', label: '5 overs' },
  { key: 'overs10', label: '10 overs' },
  { key: 'overs20', label: '20 overs' },
  { key: 'overs30', label: '30 overs' },
  { key: 'overs50', label: '50 overs' },
  { key: 'fallback', label: 'Fallback (any other length)' },
];

/** The value when it is a finite number; null for an empty box or anything else. */
export const finiteNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/** Rounds to 2 decimals the way the server does. */
export const round2 = (value: number): number => Math.round(value * 100) / 100;

/** A number for help text: '?' while its box is empty, float noise trimmed (0.7 × 3 → 2.1). */
export const formatNumber = (value: unknown): string => {
  const n = finiteNumber(value);
  return n === null ? '?' : String(Math.round(n * 1e4) / 1e4);
};

export const formatRange = ([min, max]: readonly [number, number]): string =>
  `[${min.toFixed(2)}, ${max.toFixed(2)}]`;

export interface GddRangeLimits {
  floor: number;
  minWidth: number;
  cap: number;
}

/**
 * The server's rule: average + band, then kept valid and rounded to 2 decimals.
 * min = round2(clamp(avg + band.min, floor, cap − minWidth));
 * max = round2(min(cap, max(avg + band.max, min + minWidth))).
 */
export const gddRange = (
  average: number,
  band: { min: number; max: number },
  limits: GddRangeLimits
): [number, number] => {
  const min = round2(
    Math.min(Math.max(average + band.min, limits.floor), limits.cap - limits.minWidth)
  );
  const max = round2(Math.min(limits.cap, Math.max(average + band.max, min + limits.minWidth)));
  return [min, max];
};

export interface GddPreviewRange {
  range: [number, number];
  /** average + band, before the range limits and the rounding. */
  raw: [number, number];
  /** True when the range limits moved the range (more than rounding did). */
  limited: boolean;
}

/** The AI target for one tier and side from an average; null while a value it needs is empty. */
export const previewRange = (
  gdd: GddRangesDraft | null | undefined,
  average: unknown,
  tier: MatchdayDirectorGddTier,
  side: GddSide
): GddPreviewRange | null => {
  const avg = finiteNumber(average);
  const band = gdd?.bands?.[tier]?.[side];
  const bandMin = finiteNumber(band?.min);
  const bandMax = finiteNumber(band?.max);
  const floor = finiteNumber(gdd?.rangeLimits?.floor);
  const minWidth = finiteNumber(gdd?.rangeLimits?.minWidth);
  const cap = finiteNumber(gdd?.rangeLimits?.cap);
  if (
    avg === null ||
    bandMin === null ||
    bandMax === null ||
    floor === null ||
    minWidth === null ||
    cap === null
  ) {
    return null;
  }
  const range = gddRange(avg, { min: bandMin, max: bandMax }, { floor, minWidth, cap });
  const raw: [number, number] = [avg + bandMin, avg + bandMax];
  return {
    range,
    raw,
    limited: round2(raw[0]) !== range[0] || round2(raw[1]) !== range[1],
  };
};

/**
 * Whether the server would use a player's own average (given enough balls on
 * that side): averages.min ≤ average ≤ averages.max. Null while a value is empty.
 */
export const isUsableAverage = (
  gdd: GddRangesDraft | null | undefined,
  average: unknown
): boolean | null => {
  const avg = finiteNumber(average);
  const min = finiteNumber(gdd?.averages?.min);
  const max = finiteNumber(gdd?.averages?.max);
  if (avg === null || min === null || max === null) return null;
  return avg >= min && avg <= max;
};

/** A tier's dF range as text, e.g. "0.9 ≤ dF ≤ 1.2" ('?' for an empty threshold). */
export const tierCondition = (
  tier: MatchdayDirectorGddTier,
  thresholds: GddThresholdsDraft
): string => {
  const mediumFrom = formatNumber(thresholds?.mediumFrom);
  const hardAbove = formatNumber(thresholds?.hardAbove);
  const veryHardAbove = formatNumber(thresholds?.veryHardAbove);
  switch (tier) {
    case 'easy':
      return `dF < ${mediumFrom}`;
    case 'medium':
      return `${mediumFrom} ≤ dF ≤ ${hardAbove}`;
    case 'hard':
      return `${hardAbove} < dF ≤ ${veryHardAbove}`;
    case 'very_hard':
      return `dF > ${veryHardAbove}`;
  }
};

/**
 * The highest dF a player can reach: a 100% win rate with no churn, so
 * baseDifficulty × scaleMultiplier. Rounded to 6 decimals, as the server rounds
 * dF before picking a tier. Null while either is empty or negative.
 */
export const maxDifficultyFactor = (
  baseDifficulty: unknown,
  scaleMultiplier: unknown
): number | null => {
  const base = finiteNumber(baseDifficulty);
  const scale = finiteNumber(scaleMultiplier);
  if (base === null || scale === null || base < 0 || scale < 0) return null;
  return Math.round(base * scale * 1e6) / 1e6;
};

/**
 * Tiers that need a higher dF than any player reaches (dF ≤ `maxDF`), in tier
 * order. Lowering their threshold below `maxDF` makes them reachable. Easy
 * needs no minimum, so it is never listed.
 */
export const unreachableTiers = (
  thresholds: GddThresholdsDraft,
  maxDF: number
): MatchdayDirectorGddTier[] => {
  const mediumFrom = finiteNumber(thresholds?.mediumFrom);
  const hardAbove = finiteNumber(thresholds?.hardAbove);
  const veryHardAbove = finiteNumber(thresholds?.veryHardAbove);
  const tiers: MatchdayDirectorGddTier[] = [];
  if (mediumFrom !== null && mediumFrom > maxDF) tiers.push('medium'); // needs dF ≥ mediumFrom
  if (hardAbove !== null && hardAbove >= maxDF) tiers.push('hard'); // needs dF > hardAbove
  if (veryHardAbove !== null && veryHardAbove >= maxDF) tiers.push('very_hard'); // dF > veryHardAbove
  return tiers;
};

/**
 * Tiers that no dF can fall into because of the thresholds themselves, with
 * the reason: Easy when Medium starts at 0 (dF is never below 0), and Hard
 * when Hard above equals Very Hard above (dF > x and dF ≤ x is empty).
 */
export const emptyTiers = (
  thresholds: GddThresholdsDraft
): { tier: MatchdayDirectorGddTier; reason: string }[] => {
  const mediumFrom = finiteNumber(thresholds?.mediumFrom);
  const hardAbove = finiteNumber(thresholds?.hardAbove);
  const veryHardAbove = finiteNumber(thresholds?.veryHardAbove);
  const tiers: { tier: MatchdayDirectorGddTier; reason: string }[] = [];
  if (mediumFrom !== null && mediumFrom <= 0) {
    tiers.push({ tier: 'easy', reason: 'Medium from is 0, and dF is never below 0' });
  }
  if (hardAbove !== null && veryHardAbove !== null && hardAbove >= veryHardAbove) {
    tiers.push({ tier: 'hard', reason: 'Hard above equals Very Hard above' });
  }
  return tiers;
};

/** What a tier needs, e.g. "dF above 2" ('?' for an empty threshold). */
export const tierRequirement = (
  tier: MatchdayDirectorGddTier,
  thresholds: GddThresholdsDraft
): string => {
  switch (tier) {
    case 'easy':
      return `dF below ${formatNumber(thresholds?.mediumFrom)}`;
    case 'medium':
      return `dF of at least ${formatNumber(thresholds?.mediumFrom)}`;
    case 'hard':
      return `dF above ${formatNumber(thresholds?.hardAbove)}`;
    case 'very_hard':
      return `dF above ${formatNumber(thresholds?.veryHardAbove)}`;
  }
};

/**
 * GDD values the Unity client copies for its fallback (used when a server range
 * is missing or invalid): the bands, the usable-average window, the range limits
 * and the default averages. The client team asked (9 Oct 2026) to be told before
 * any of them changes, so the two copies stay the same. Tier thresholds and the
 * minimum balls are not copied: the client takes the tier from `difficulty` and
 * gets no ball counts.
 */
export const CLIENT_COPIED_GDD_PATHS = [
  'gddRanges.bands',
  'gddRanges.averages.min',
  'gddRanges.averages.max',
  'gddRanges.rangeLimits',
  'gddRanges.defaultAverages',
] as const;

/** Whether any changed field (a dot path) is one the client keeps a copy of. */
export const touchesClientCopiedGdd = (fields: readonly string[]): boolean =>
  fields.some((field) =>
    CLIENT_COPIED_GDD_PATHS.some((path) => field === path || field.startsWith(`${path}.`))
  );
